import moment from 'moment';
import React from "react";
import { useSelector } from 'react-redux';

const scaleMap = {
    million: 1_000_000,
    billion: 1_000_000_000
};

export const DEFAULT_COLUMN_WIDTH = 150;
export const MIN_COLUMN_WIDTH = 100;
export const defaultColumnWidths = {
    accountname: 240,
    parentaccount: 220,
    accountowner: 220,
    website: 200,
    createdby: 160,
    modifiedby: 160,
};

const universalSorter = (valueA, valueB, profile) => {
    const extractText = (val) => {
        if (React.isValidElement(val)) {
            val = val.props?.children ?? '';
        }
        if (Array.isArray(val)) {
            val = val.map(child => {
                if (React.isValidElement(child)) {
                    return child.props?.children ?? '';
                }
                return child;
            }).join(' ');
        }
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
            const trimmed = val.trim();
            if (profile?.dateFormate && moment(trimmed, profile.dateFormate, true).isValid()) {
                return moment(trimmed, profile.dateFormate, true).toDate().getTime();
            }
            const scaleMatch = trimmed.match(/(\d+(?:\.\d+)?)\s*(million|billion)/i);
            if (scaleMatch) {
                const number = parseFloat(scaleMatch[1]);
                const unit = scaleMatch[2].toLowerCase();
                return number * scaleMap[unit];
            }
            const cleaned = trimmed
                .replace(/^[^\d-]+/, '')
                .replace(/,/g, '')
                .replace(/\s+/g, '');
            const numberMatch = cleaned.match(/^(\d+(\.\d+)?)/);
            if (numberMatch) {
                return parseFloat(numberMatch[0]);
            }
            return trimmed.toLowerCase();
        }
        return '';
    };
    const a = extractText(valueA);
    const b = extractText(valueB);
    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }
    return String(a).localeCompare(String(b), undefined, { numeric: true });
};
export const generateDynamicColumns = (checkboxValues, ViewDetailPage, sortedInfo, columnWidths, onColumnResize, onColumnResizeEnd) => {
    const localData = useSelector((state) => state.localRedux);
    const Role = localData.role
    const ProfileData = localData.profile
    const profile = ProfileData
    let columns = [];
    if (checkboxValues !== undefined) {
        columns = checkboxValues
            .filter((item) => item.isChecked)
            .map((item) => {
                const resolvedWidth = columnWidths?.[item.FieldValue]
                    || defaultColumnWidths[item.FieldValue]
                    || DEFAULT_COLUMN_WIDTH;
                const column = {
                    title: item.name,
                    dataIndex: item.FieldValue,
                    key: item.FieldValue,
                    width: resolvedWidth,
                    onHeaderCell: (col) => ({
                        width: col.width,
                        minWidth: MIN_COLUMN_WIDTH,
                        onResize: (newWidth) => onColumnResize && onColumnResize(item.FieldValue, newWidth),
                        onResizeEnd: (newWidth) => onColumnResizeEnd && onColumnResizeEnd(item.FieldValue, newWidth),
                    }),
                    sortDirections: ['ascend', 'descend'],
                    sortOrder: sortedInfo?.column?.dataIndex === item.FieldValue ? sortedInfo?.order : null
                };
                switch (item.FieldValue) {
                    case 'parentaccount':
                        column.onCell = (record) => ({
                            onClick: (e) => {
                                e.stopPropagation();
                                if (record?.LinkAccount) {
                                    ViewDetailPage(record.accountID, "ACCOUNT")
                                }
                            },
                        });
                        column.sorter = (a, b) => {
                            const extractNameText = (item) => {
                                const children = item?.parentaccount?.props?.children || [];
                                const figcaption = children.find(child => child && child.type === 'figcaption');
                                return figcaption ? figcaption.props.children[0].props.children.trim() : '';
                            };
                            const valueA = extractNameText(a) || '';
                            const valueB = extractNameText(b) || '';
                            return valueA.localeCompare(valueB);
                        };
                        break;
                    case 'accountowner':
                        column.onCell = (record) => ({
                            onClick: (e) => {
                                e.stopPropagation();
                                if (record.userId) {
                                    if (Role === "Administrator" || profile.userId === record.userId) {
                                        ViewDetailPage(record.userId, "USER")
                                    }
                                }
                                else {
                                    ViewDetailPage(record.key, "ACCOUNT");
                                }
                            },
                        });
                        column.sorter = (a, b) => {
                            const extractNameText = (item) => {
                                const children = item?.accountowner?.props?.children || [];
                                const figcaption = children.find(child => child && child.type === 'figcaption');
                                // const figcaption = item.accountowner.props.children.find(child => child.type === 'figcaption');
                                return figcaption ? figcaption.props.children[0].props.children.trim() : ''; // Extract first div inside figcaption
                            };
                            const valueA = extractNameText(a);
                            const valueB = extractNameText(b);
                            return valueA.localeCompare(valueB);
                        };
                        break;
                    case 'accountname':
                        column.sorter = (a, b) => {
                            const extractNameText = (item) => {
                                const figcaption = item.accountname.props.children.find(child => child.type === 'figcaption');
                                return figcaption ? figcaption.props.children[0].props.children.trim() : ''; // Extract first div inside figcaption
                            };
                            const valueA = extractNameText(a);
                            const valueB = extractNameText(b);
                            return valueA.localeCompare(valueB);
                        };
                        break;
                    default:
                        column.sorter = (a, b) => {
                            const valueA = a[item.FieldValue]?.props?.children || '';
                            const valueB = b[item.FieldValue]?.props?.children || '';
                            return universalSorter(valueA, valueB, profile);
                        };
                        break;
                }
                return column;
            });
    }
    return columns;
};
const ellipsisClass = 'cell-ellipsis';

export const generateUsersTableData = (exportData) => {
    const Role = localStorage.getItem("cx_role")
    const localData = useSelector((state) => state.localRedux);
    const ProfileData = localData.profile
    const profile = ProfileData
    const usersTableData = [];
    if (exportData) {
        exportData.forEach((account, key) => {
            usersTableData.push({
                LinkAccount: account.parentAccountPermission,
                AccountDataForPermission: account,
                SharingData: account?.sharingPermission?.sharePermission,
                sharingid: account.fromId,
                userId: account.account_owner,
                accountID: account.parent_organization ? account.parent_organization : account.account_id,
                key: account.account_id,
                no: key + 1,
                accountname: (
                    <div className="user-info_image table_list_clicks">
                        {account.account_id && account.account_profile !== null && account.account_profile !== undefined && account.account_profile !== "" ?
                            < figure >
                                <img style={{ width: "30px", height: "30px" }} src={account.account_profile} alt="" />
                            </figure>
                            :
                            <span className='list_span_Tag AccountIcon'>
                                {account.account_name && account.account_name.slice(0, 1).toUpperCase()}
                            </span>
                        }
                        {account.account_id &&
                            <figcaption>
                                <div className={ellipsisClass} title={account.account_name}  >
                                    {account.account_name}
                                </div>
                                <div className={`entityemail ${ellipsisClass}`} title={account.account_number}>
                                    {account.account_number}
                                </div>
                            </figcaption>
                        }
                    </div >),
                accountnumber: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.account_number}</div>),
                parentaccount: (
                    <div className={`user-info_image ${account.parentAccountPermission && "ListNavigate"}`}>
                        {account.parent_organization &&
                            (account.parentAccountProfile !== null && account.parentAccountProfile !== undefined && account.parentAccountProfile !== "" ?
                                < figure >
                                    <img style={{ width: "30px", height: "30px" }} src={account.parentAccountProfile} alt="" />
                                </figure>
                                :
                                <span className='list_span_Tag AccountIcon'>
                                    {account.parentAccount && account.parentAccount.slice(0, 1).toUpperCase()}
                                </span>)
                        }
                        {account.parent_organization &&
                            <figcaption>
                                <div className={ellipsisClass} title={account.parentAccount}  >
                                    {account.parentAccount}
                                </div>
                                <div className={`entityemail ${ellipsisClass}`} title={account.parentAccountNumber}>
                                    {account.parentAccountNumber}
                                </div>
                            </figcaption>
                        }
                    </div >
                ),
                accountowner: (<div className={`user-info_image ${(Role === "Administrator" || profile.userId === account.account_owner) && "ListNavigate"}`}>
                    {account.account_owner &&
                        (account.userProfilePic !== null && account.userProfilePic !== undefined && account.userProfilePic !== "" ?
                            < figure >
                                <img style={{ width: "30px", height: "30px" }} src={account.userProfilePic} alt="" />
                            </figure>
                            :
                            <span className='list_span_Tag UserIcon'>
                                {account.userFirstName && account.userFirstName.slice(0, 1).toUpperCase()}
                            </span>
                        )}
                    {account.account_owner &&
                        <figcaption>
                            <div className={ellipsisClass} title={`${account.userFirstName} ${account.userLastName}`} >
                                {`${account.userFirstName} ${account.userLastName}`}
                            </div>
                            <div className={`entityemail ${ellipsisClass}`} title={account.accountOwner}>
                                {account?.accountOwner}
                            </div>
                        </figcaption>
                    }
                </div>),
                website: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.website}</div>),
                type: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.account_type_name}</div>),
                industry: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.industry_name}</div>),
                ownership: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.ownership_name}</div>),
                // description: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.description ? `${account.description.substring(0, 50)}...` : account.description}</div>),
                // qualified: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.qualified}</div>),
                noofemployees: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.nom_of_employees}</div>),
                annualrevenue: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.annual_revenue}</div>),
                customerStatus: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.customer_status}</div>),
                rating: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.rating}</div>),
                country: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.country}</div>),
                state: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.state}</div>),
                city: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.city}</div>),
                street: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.street}</div>),
                zipCode: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.postcode}</div>),
                registeredName: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.registered_name}</div>),
                registrationNumber: (<div className={`table_list_clicks ${ellipsisClass}`}>{account.registration_number}</div>),
                createdby: (<div className={`table_list_clicks ${ellipsisClass}`}>
                    {account?.created_at}
                </div>),
                modifiedby: (<div className={`table_list_clicks ${ellipsisClass}`}>
                    {account?.modified_at}
                </div>),
                accountScore: (
                    <div className={`table_list_clicks ${ellipsisClass} status-text LeadSource`}>
                        {Number(account?.account_score)?.toFixed(2)}
                    </div >
                ),
            });
        });
    }

    return usersTableData;
};